import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import LockIcon from "@mui/icons-material/Lock";
import {
  Box,
  Card,
  CardContent,
  Container,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  Grid,
} from "@mui/material";
import { motion } from "framer-motion";
import { LoadingButton } from "@mui/lab";
import BrandingLogo from "components/branding-logo";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store";
import { setNewUser } from "../../features/register/registerSlice";

type InitialValues = {
  documentType: string;
  documentNumber: string;
  serviceRequired: string;
  serviceUsage: string;
  hearAboutUs: string;
  employmentStatus: string;
  sourceOfFunds: string;
  industry: string;
  email: string;
  phone: string;
};

const ExtendedIdentificationQuestionnaire = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const newUser = useAppSelector((state) => state.register.newUser);

  const initialValues: InitialValues = {
    documentType: "",
    documentNumber: "",
    serviceRequired: "",
    serviceUsage: "",
    hearAboutUs: "",
    employmentStatus: "",
    sourceOfFunds: "",
    industry: "",
    email: "",
    phone: "",
  };

  if (!newUser) {
    navigate("/signin");
  } else {
    initialValues.email = newUser.email;
    initialValues.phone = newUser.phone;
  }

  const validationSchema = Yup.object({
    documentType: Yup.string().required("Choose document type"),
    documentNumber: Yup.string().required("Enter document number"),
    serviceRequired: Yup.string().required("Choose service required"),
    serviceUsage: Yup.string().required("Choose goal usage"),
    hearAboutUs: Yup.string().required("Required"),
    employmentStatus: Yup.string().required("Required"),
    sourceOfFunds: Yup.string().required("Required"),
    industry: Yup.string().required("Required"),
  });

  const onSubmit = (values: InitialValues) => {
    const formData = {
      documentType: values.documentType,
      documentNumber: values.documentNumber,
      serviceRequired: values.serviceRequired,
      serviceUsage: values.serviceUsage,
      hearAboutUs: values.hearAboutUs,
      employmentStatus: values.employmentStatus,
      sourceOfFunds: values.sourceOfFunds,
      industry: values.industry,
    };

    const form1_json = JSON.stringify(formData);

    dispatch(
      setNewUser({
        first_name: newUser?.first_name ?? "",
        last_name: newUser?.last_name ?? "",
        email: values.email,
        kyc_program_id: newUser?.kyc_program_id ?? null,
        phone: values.phone,
        country: newUser?.country ?? "",
        citizenship_country: newUser?.citizenship_country ?? "",
        user_birth_date: newUser?.user_birth_date ?? "",
        gender: newUser?.gender ?? "",
        password: newUser?.password ?? null,
        currency: newUser?.currency ?? "",
        city: newUser?.city ?? "",
        address: newUser?.address ?? "",
        state: newUser?.state ?? "",
        zip_code: newUser?.zip_code ?? "",
        form1_json,
        form3_json: null,
        form4_json: null,
      })
    );
    navigate("/form-first-declaration");
  };

  const { handleSubmit, getFieldMeta, handleChange, handleBlur, isSubmitting } =
    useFormik<InitialValues>({
      initialValues,
      validationSchema,
      onSubmit,
    });

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{ mt: 4 }}
    >
      <Container maxWidth="sm">
        <form onSubmit={handleSubmit}>
          <Card elevation={24} component={motion.div} layoutId="auth-card">
            <Box paddingY={2.5} paddingX={4} sx={{ bgcolor: "brand.auth" }}>
              <BrandingLogo width={200} />
            </Box>
            <CardContent>
              <Stack spacing={4}>
                <Box display="flex" flexDirection="row" alignItems="center">
                  <LockIcon fontSize="large" />
                  <Typography variant="h6" fontWeight="600" ml={1}>
                    Identification & Survey Questionnaire
                  </Typography>
                </Box>
                <Grid container rowSpacing={2} columnSpacing={1}>
                  <Grid item xs={12}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend">
                        Identification Document Type
                      </FormLabel>
                      <RadioGroup
                        row
                        name="documentType"
                        value={getFieldMeta("documentType").value}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      >
                        <FormControlLabel
                          value="passport"
                          control={<Radio />}
                          label="Passport"
                        />
                        <FormControlLabel
                          value="id_card"
                          control={<Radio />}
                          label="Identification Card"
                        />
                      </RadioGroup>
                      {getFieldMeta("documentType").touched &&
                        getFieldMeta("documentType").error && (
                          <Typography variant="caption" color="error">
                            {getFieldMeta("documentType").error}
                          </Typography>
                        )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Passport/ID Number"
                      name="documentNumber"
                      variant="outlined"
                      fullWidth
                      value={getFieldMeta("documentNumber").value}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(
                        getFieldMeta("documentNumber").touched &&
                          getFieldMeta("documentNumber").error
                      )}
                      helperText={
                        getFieldMeta("documentNumber").touched &&
                        getFieldMeta("documentNumber").error
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend">
                        Services Required
                      </FormLabel>
                      <RadioGroup
                        row
                        name="serviceRequired"
                        value={getFieldMeta("serviceRequired").value}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      >
                        <FormControlLabel
                          value="exchange"
                          control={<Radio />}
                          label="Exchange"
                        />
                        <FormControlLabel
                          value="wallet"
                          control={<Radio />}
                          label="Wallet"
                        />
                        <FormControlLabel
                          value="both"
                          control={<Radio />}
                          label="Both"
                        />
                      </RadioGroup>
                      {getFieldMeta("serviceRequired").touched &&
                        getFieldMeta("serviceRequired").error && (
                          <Typography variant="caption" color="error">
                            {getFieldMeta("serviceRequired").error}
                          </Typography>
                        )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend">
                        What will you use iLedgends s.r.o. services for?
                      </FormLabel>
                      <RadioGroup
                        row
                        name="serviceUsage"
                        value={getFieldMeta("serviceUsage").value}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      >
                        <FormControlLabel
                          value="Investment"
                          control={<Radio />}
                          label="Investment"
                        />
                        <FormControlLabel
                          value="Speculation"
                          control={<Radio />}
                          label="Speculation"
                        />
                        <FormControlLabel
                          value="Payment"
                          control={<Radio />}
                          label="Payment"
                        />
                        <FormControlLabel
                          value="Store_crypto_assets"
                          control={<Radio />}
                          label="Store crypto assets"
                        />
                      </RadioGroup>
                      {getFieldMeta("serviceUsage").touched &&
                        getFieldMeta("serviceUsage").error && (
                          <Typography variant="caption" color="error">
                            {getFieldMeta("serviceUsage").error}
                          </Typography>
                        )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="How did you hear about us?"
                      name="hearAboutUs"
                      variant="outlined"
                      fullWidth
                      value={getFieldMeta("hearAboutUs").value}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(
                        getFieldMeta("hearAboutUs").touched &&
                          getFieldMeta("hearAboutUs").error
                      )}
                      helperText={
                        getFieldMeta("hearAboutUs").touched &&
                        getFieldMeta("hearAboutUs").error
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend">
                        What is your Employment Status?
                      </FormLabel>
                      <RadioGroup
                        row
                        name="employmentStatus"
                        value={getFieldMeta("employmentStatus").value}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      >
                        <FormControlLabel
                          value="Student"
                          control={<Radio />}
                          label="Student"
                        />
                        <FormControlLabel
                          value="Employed"
                          control={<Radio />}
                          label="Employed"
                        />
                        <FormControlLabel
                          value="Unemployed"
                          control={<Radio />}
                          label="Unemployed"
                        />
                        <FormControlLabel
                          value="Retired"
                          control={<Radio />}
                          label="Retired"
                        />
                        <FormControlLabel
                          value="Business_owner"
                          control={<Radio />}
                          label="Business owner"
                        />
                        <FormControlLabel
                          value="Self-employed"
                          control={<Radio />}
                          label="Self-employed"
                        />
                      </RadioGroup>
                      {getFieldMeta("employmentStatus").touched &&
                        getFieldMeta("employmentStatus").error && (
                          <Typography variant="caption" color="error">
                            {getFieldMeta("employmentStatus").error}
                          </Typography>
                        )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend">
                        What is your Source of Wealth?
                      </FormLabel>
                      <RadioGroup
                        row
                        name="sourceOfFunds"
                        value={getFieldMeta("sourceOfFunds").value}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      >
                        <FormControlLabel
                          value="Occupation"
                          control={<Radio />}
                          label="Occupation"
                        />
                        <FormControlLabel
                          value="Savings"
                          control={<Radio />}
                          label="Savings"
                        />
                        <FormControlLabel
                          value="Credit_Loan"
                          control={<Radio />}
                          label="Credit/Loan"
                        />
                        <FormControlLabel
                          value="Investments"
                          control={<Radio />}
                          label="Investments"
                        />
                        <FormControlLabel
                          value="Inheritance"
                          control={<Radio />}
                          label="Inheritance"
                        />
                        <FormControlLabel
                          value="Family_or_others"
                          control={<Radio />}
                          label="Family or others"
                        />
                      </RadioGroup>
                      {getFieldMeta("sourceOfFunds").touched &&
                        getFieldMeta("sourceOfFunds").error && (
                          <Typography variant="caption" color="error">
                            {getFieldMeta("sourceOfFunds").error}
                          </Typography>
                        )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="What industry do you work in?"
                      name="industry"
                      variant="outlined"
                      fullWidth
                      value={getFieldMeta("industry").value}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(
                        getFieldMeta("industry").touched &&
                          getFieldMeta("industry").error
                      )}
                      helperText={
                        getFieldMeta("industry").touched &&
                        getFieldMeta("industry").error
                      }
                    />
                  </Grid>
                </Grid>
                <Stack spacing={2}>
                  <LoadingButton
                    fullWidth
                    loading={isSubmitting}
                    variant="contained"
                    type="submit"
                  >
                    Next Step
                  </LoadingButton>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </form>
      </Container>
    </Box>
  );
};

export default ExtendedIdentificationQuestionnaire;
